import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import {
  Tournament,
  Person,
  ParticipantDto,
  PersonClient,
  ParticipantClient,
  Gender,
  ConfigClient,
} from '../../utils/apiClient';
import { ReactSelectDefault } from '../../common/components/ReactSelectDefault/ReactSelect';
import { SelectOption } from '../../common/types';
import './AdminParticipantModal.scss';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import {
  formatAgeCategoryLabel,
  formatWeightCategoryLabel,
  resolveAgeCategory,
  resolveWeightCategory,
} from '../../utils/categoryResolver';
import { filterAgeByGender, filterWeightByGender } from '../../utils/genderCategoryUtils';

const DEFAULT_RANKS = [
  'Б/р', '3 юн.', '2 юн.', '1 юн.',
  '3 разряд', '2 разряд', '1 разряд',
  'КМС', 'МС', 'МСМК', 'ЗМС', 'Другое',
];

interface AdminParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
  onParticipantAdded: () => void;
}

export const AdminParticipantModal: React.FC<AdminParticipantModalProps> = ({
  isOpen,
  onClose,
  tournament,
  onParticipantAdded
}) => {
  const appContext = React.useContext(AppContext);
  const [athletes, setAthletes] = useState<Person[]>([]);
  const [coaches, setCoaches] = useState<Person[]>([]);
  const [registeredParticipants, setRegisteredParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const personClient = new PersonClient(ApiUrl, axios);
  const participantClient = new ParticipantClient(ApiUrl, axios);
  const configClient = new ConfigClient(ApiUrl, axios);

  const [rankOptions, setRankOptions] = useState<string[]>(DEFAULT_RANKS);

  const [formData, setFormData] = useState<ParticipantDto>(new ParticipantDto({
    personId: 0,
    tournamentId: tournament.id!,
    weight: 0,
    organization: '',
    coachId: undefined,
    rank: '1 юн.'
  }));

  const [selectedWeightCategoryId, setSelectedWeightCategoryId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [participantsData, personsData, config] = await Promise.all([
        participantClient.getParticipants(tournament.id),
        personClient.getPersons(),
        configClient.getConfig().catch(() => null),
      ]);

      setRegisteredParticipants(participantsData);

      if (config?.rankOptions?.length) setRankOptions(config.rankOptions);

      const athletesList = personsData.filter(person =>
        person.personRoles?.some(role => role.role?.name === 'Спортсмен')
      );
      const coachesList = personsData.filter(person =>
        person.personRoles?.some(role => role.role?.name === 'Тренер')
      );

      setAthletes(athletesList);
      setCoaches(coachesList);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setLoading(false);
    }
  };

  const isAthleteRegistered = (personId: number) => {
    return registeredParticipants.some(p => p.personId === personId);
  };

  const calculateAgeAtTournament = (birthDate: Date) => {
    if (!tournament) return 0;
    const targetDate = new Date(tournament.startDate);
    let age = targetDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = targetDate.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && targetDate.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (!tournament || !formData.weight || formData.weight <= 0) return;

    const athlete = athletes.find((a) => a.id === formData.personId);
    const matchingWeightCategory = resolveWeightCategory(
      formData.weight,
      tournament.weightCategories ?? [],
      athlete?.gender
    );
    setSelectedWeightCategoryId(matchingWeightCategory?.id);
    setFormData(new ParticipantDto({
      ...formData,
      weightCategoryInTournamentId: matchingWeightCategory?.id,
    }));
  }, [formData.weight, tournament]);

  useEffect(() => {
    if (!tournament || !formData.personId) return;
    const athlete = athletes.find(a => a.id === formData.personId);
    if (!athlete) return;

    const age = calculateAgeAtTournament(new Date(athlete.birthDate));
    const matchingAgeCategory = resolveAgeCategory(age, tournament.ageCategories ?? [], athlete.gender);

    setFormData(new ParticipantDto({
      ...formData,
      ageCategoryInTournamentId: matchingAgeCategory?.id
    }));
  }, [formData.personId, tournament]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'weight') {
      const weight = value === '' ? 0 : parseFloat(value);
      setFormData(new ParticipantDto({ ...formData, weight }));
    } else if (name === 'organization') {
      setFormData(new ParticipantDto({ ...formData, organization: value }));
    }
  };

  const resetForm = () => {
    setFormData(new ParticipantDto({
      personId: 0,
      tournamentId: tournament.id!,
      weight: 0,
      organization: '',
      coachId: undefined,
      rank: '1 юн.'
    }));
    setSelectedWeightCategoryId(undefined);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.personId) {
      setError('Необходимо выбрать спортсмена');
      return;
    }

    if (formData.weight <= 0) {
      setError('Вес должен быть больше нуля');
      return;
    }

    try {
      if (isAthleteRegistered(formData.personId)) {
        setError('Этот спортсмен уже зарегистрирован на данном турнире');
        return;
      }

      await participantClient.createParticipant(new ParticipantDto({
        ...formData,
        weightCategoryInTournamentId: selectedWeightCategoryId ?? formData.weightCategoryInTournamentId,
      }));

      const updatedParticipants = await participantClient.getParticipants(tournament.id);
      setRegisteredParticipants(updatedParticipants);

      resetForm();
      setSuccess('Участник успешно зарегистрирован');
    } catch (ex) {
      // eslint-disable-next-line
      console.log(ex);
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleFinish = () => {
    resetForm();
    onParticipantAdded();
    onClose();
  };

  if (!isOpen) return null;

  const selectedAthlete = athletes.find((a) => a.id === formData.personId);
  const filteredAgeCategories = filterAgeByGender(tournament.ageCategories ?? [], selectedAthlete?.gender);
  const filteredWeightCategories = filterWeightByGender(tournament.weightCategories ?? [], selectedAthlete?.gender);

  return (
    <div className="admin-participant-modal-overlay" onClick={handleCancel}>
      <div className="admin-participant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-participant-modal__header">
          <h2>Регистрация участника</h2>
          <button className="admin-participant-modal__close" onClick={handleCancel}>
            ×
          </button>
        </div>

        <div className="admin-participant-modal__content">
          {loading ? (
            <div className="admin-participant-modal__loading">Загрузка...</div>
          ) : (
            <>
              {error && <div className="admin-participant-modal__error">{error}</div>}
              {success && <div className="admin-participant-modal__success">{success}</div>}

              <form onSubmit={handleSubmit} className="admin-participant-modal__form">
                <div className="admin-participant-modal__form-row">
                  <div className="admin-participant-modal__form-group">
                    <label>Спортсмен<span className="required">*</span></label>
                    <ReactSelectDefault<number>
                      options={athletes
                        .filter(athlete => !isAthleteRegistered(athlete.id!))
                        .map(athlete => {
                          const birth = new Date(athlete.birthDate);
                          const target = new Date(tournament.startDate);
                          let age = target.getFullYear() - birth.getFullYear();
                          const monthDiff = target.getMonth() - birth.getMonth();
                          if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < birth.getDate())) age--;
                          return {
                            value: athlete.id!,
                            label: `${athlete.lastName} ${athlete.firstName} ${athlete.patronymic || ''} (${age} лет)`
                          };
                        })}
                      value={formData.personId || undefined}
                      onChange={(value) => setFormData(new ParticipantDto({
                        ...formData,
                        personId: value.value,
                      }))}
                    placeholder="Выберите спортсмена"
                    params={{ width: 100, height: 42 }}
                    isClearable
                    error={false}
                    />
                  </div>

                  <div className="admin-participant-modal__form-group">
                    <label>Вес (кг)<span className="required">*</span></label>
                    <input
                      type="number"
                      name="weight"
                      min="0"
                      max="200"
                      step="0.1"
                      value={formData.weight || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="admin-participant-modal__form-row">
                  <div className="admin-participant-modal__form-group">
                    <label>Спортивная школа<span className="required">*</span></label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="admin-participant-modal__form-group">
                    <label>Тренер</label>
                    <ReactSelectDefault<number>
                      options={coaches.map(coach => ({
                        value: coach.id!,
                        label: `${coach.lastName} ${coach.firstName} ${coach.patronymic || ''}`
                      }))}
                      value={formData.coachId}
                      onChange={(option) => setFormData(new ParticipantDto({
                        ...formData,
                        coachId: option.value || undefined
                      }))}
                      placeholder="Нет тренера"
                      params={{ width: 100, height: 42 }}
                      isClearable
                      error={false}
                    />
                  </div>
                </div>

                <div className="admin-participant-modal__form-row">
                  <div className="admin-participant-modal__form-group">
                    <label>Разряд</label>
                    <ReactSelectDefault<string>
                      options={rankOptions.map(r => ({ value: r, label: r }))}
                      value={formData.rank || undefined}
                      onChange={(option) => setFormData(new ParticipantDto({
                        ...formData,
                        rank: option.value || ''
                      }))}
                      placeholder="Выберите разряд"
                      params={{ width: 100, height: 42 }}
                      isClearable
                      error={false}
                    />
                  </div>

                  <div className="admin-participant-modal__form-group">
                    <label>Возрастная категория</label>
                    <ReactSelectDefault<number>
                      options={filteredAgeCategories.map(ac => ({
                        value: ac.id!,
                        label: formatAgeCategoryLabel(ac, tournament.ageCategories ?? [])
                      })) || []}
                      value={formData.ageCategoryInTournamentId}
                      onChange={(option) => setFormData(new ParticipantDto({
                        ...formData,
                        ageCategoryInTournamentId: option.value || undefined
                      }))}
                      placeholder="Не определена"
                      params={{ width: 100, height: 42 }}
                      isClearable={false}
                      disabled
                      error={false}
                    />
                  </div>
                </div>

                <div className="admin-participant-modal__form-row">
                  <div className="admin-participant-modal__form-group admin-participant-modal__form-group--full">
                    <label>Весовая категория</label>
                    <ReactSelectDefault<number>
                      options={filteredWeightCategories.map(wc => ({
                        value: wc.id!,
                        label: formatWeightCategoryLabel(wc, tournament.weightCategories ?? [])
                      })) || []}
                      value={selectedWeightCategoryId}
                      onChange={(option) => setSelectedWeightCategoryId(option.value || undefined)}
                      placeholder="Не определена"
                      params={{ width: 100, height: 42 }}
                      isClearable={false}
                      disabled
                      error={false}
                    />
                  </div>
                </div>

                <div className="admin-participant-modal__actions">
                  <button type="button" onClick={handleCancel} className="admin-participant-modal__btn admin-participant-modal__btn--cancel">
                    Отмена
                  </button>
                  <div className="admin-participant-modal__actions-right">
                    <button type="submit" className="admin-participant-modal__btn admin-participant-modal__btn--save">
                      Сохранить
                    </button>
                    <button type="button" onClick={handleFinish} className="admin-participant-modal__btn admin-participant-modal__btn--finish">
                      Завершить
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
